// 06/2026 — Platz-Ansicht (Stage 2 Beauty): zeigt pro Ressource/Platz/Stuhl
// eine eigene Spalte und stellt dar, wer wann dort belegt. Parallele
// Einwirkzeiten (zwei verschiedene Plätze gleichzeitig) werden so auf einen
// Blick sichtbar. Aktive Arbeit vs. Einwirkzeit wird – wie im Mitarbeiter-
// Kalender – durch eine schraffierte Überlagerung optisch getrennt.

import React from 'react';
import { toLocalYMD } from './calendarUtils';

const isoToMinutes = (iso) => {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
};

// Google-Kalender-artige Spaltenzuweisung für überlappende Termine einer Spalte
const computeColumns = (appts) => {
  const events = appts
    .map((a) => ({ id: a.id, start: isoToMinutes(a.start_time), end: isoToMinutes(a.end_time) }))
    .sort((x, y) => x.start - y.start || x.end - y.end);
  const result = {};
  let cluster = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const colEnds = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let c = 0; c < colEnds.length; c += 1) {
        if (ev.start >= colEnds[c]) {
          ev.col = c;
          colEnds[c] = ev.end;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.col = colEnds.length;
        colEnds.push(ev.end);
      }
    });
    const total = colEnds.length;
    cluster.forEach((ev) => { result[ev.id] = { colIndex: ev.col, colCount: total }; });
    cluster = [];
    clusterEnd = -Infinity;
  };
  events.forEach((ev) => {
    if (cluster.length > 0 && ev.start >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.end);
  });
  flush();
  return result;
};

// Einwirkzeit-Segmente (staff_busy === false) relativ zum Terminstart
const soakSegments = (service) => {
  if (!service?.has_treatment_phases || !Array.isArray(service?.treatment_phases)) return [];
  const segs = [];
  let cursor = 0;
  service.treatment_phases.forEach((ph) => {
    const dur = parseInt(ph.duration_minutes, 10) || 0;
    if (ph.staff_busy === false && dur > 0) {
      segs.push({ offset: cursor, duration: dur, name: ph.name || 'Einwirkzeit' });
    }
    cursor += dur;
  });
  return segs;
};

export const ResourceDayView = ({
  resources,
  appointments,
  currentDate,
  timeSlots,
  slotHeight,
  calendarStartMinutes,
  serviceMap,
  getAppointmentColor,
  trainerNameById = {},
  onAppointmentClick,
}) => {
  const dateStr = toLocalYMD(currentDate);
  const pxPerMin = slotHeight / 30;

  const activeResources = (resources || []).filter((r) => r.is_active !== false);

  // Termine des Tages, die mind. einer Ressource zugeordnet sind
  const dayAppts = (appointments || []).filter((a) => {
    if (a.status === 'cancelled') return false;
    if (!a.start_time) return false;
    if (toLocalYMD(new Date(a.start_time)) !== dateStr) return false;
    return Array.isArray(a.resource_ids) && a.resource_ids.length > 0;
  });

  const apptsForResource = (rid) => dayAppts.filter((a) => (a.resource_ids || []).includes(rid));

  if (activeResources.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 text-sm" data-testid="resource-view-empty">
        Keine Plätze/Ressourcen angelegt. Lege unter „Ressourcen" Plätze (z. B. Stühle) an,
        um die Platz-Ansicht zu nutzen.
      </div>
    );
  }

  return (
    <table className="w-full border-collapse" style={{ borderSpacing: 0 }} data-testid="resource-day-view">
      <thead>
        <tr className="bg-gray-50" style={{ boxShadow: 'inset 0 -1px 0 #e5e5e5' }}>
          <th className="p-3 text-left font-semibold text-sm sticky left-0 bg-gray-50 z-10 w-24">
            Zeit
          </th>
          {activeResources.map((r) => (
            <th
              key={r.id}
              className="p-3 text-center font-semibold text-sm border-l"
              data-testid={`resource-column-header-${r.id}`}
            >
              <div className="truncate">{r.name}</div>
              {r.type && (
                <div className="text-[11px] font-normal text-gray-400 capitalize">{r.type}</div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {timeSlots.map((timeSlot, slotIndex) => (
          <tr key={timeSlot} className="hover:bg-gray-50">
            <td
              className="text-sm font-medium text-gray-600 sticky left-0 bg-white border-r"
              style={{
                height: `${slotHeight / 2}px`,
                padding: '0 8px',
                verticalAlign: 'middle',
                boxShadow: 'inset 0 -1px 0 #e5e5e5',
              }}
            >
              {timeSlot.endsWith(':00') || timeSlot.endsWith(':30') ? timeSlot : ''}
            </td>
            {activeResources.map((r) => {
              const isFirstSlot = slotIndex === 0;
              return (
                <td
                  key={`${r.id}-${timeSlot}`}
                  className="p-0 border-l"
                  style={{
                    height: `${slotHeight / 2}px`,
                    verticalAlign: 'top',
                    position: 'relative',
                    boxShadow: 'inset 0 -1px 0 #f0f0f0',
                  }}
                >
                  {isFirstSlot && (() => {
                    const list = apptsForResource(r.id);
                    const colMap = computeColumns(list);
                    return list.map((apt) => {
                      const startMin = isoToMinutes(apt.start_time);
                      const endMin = isoToMinutes(apt.end_time);
                      let top = ((startMin - calendarStartMinutes) / 30) * slotHeight;
                      let height = ((endMin - startMin) / 30) * slotHeight;
                      if (top < 0) { height += top; top = 0; }
                      if (height <= 0) return null;
                      const cc = colMap[apt.id]?.colCount ?? 1;
                      const ci = colMap[apt.id]?.colIndex ?? 0;
                      const widthPct = 100 / cc;
                      const leftPct = widthPct * ci;
                      const color = getAppointmentColor ? getAppointmentColor(apt, [], {}) : '#0d9488';
                      const names = apt.customer_names || (apt.customer_name ? [apt.customer_name] : []);
                      const trainerName = trainerNameById[apt.trainer_id] || '';
                      const service = serviceMap?.get?.(apt.service_id);
                      const segs = soakSegments(service);
                      const hhmm = (iso) => {
                        const d = new Date(iso);
                        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                      };
                      return (
                        <div
                          key={apt.id}
                          data-testid={`resource-appointment-${apt.id}`}
                          title={`${hhmm(apt.start_time)}–${hhmm(apt.end_time)} · ${apt.service_name || 'Termin'}${names.length ? ` · ${names.join(', ')}` : ''}${trainerName ? ` · ${trainerName}` : ''}`}
                          onClick={(e) => { e.stopPropagation(); onAppointmentClick?.(apt); }}
                          style={{
                            position: 'absolute',
                            top: `${top}px`,
                            left: `calc(${leftPct}% + 1px)`,
                            width: `calc(${widthPct}% - 2px)`,
                            height: `${height}px`,
                            borderRadius: '6px',
                            border: `1.5px solid ${color}`,
                            background: `${color}22`,
                            overflow: 'hidden',
                            cursor: onAppointmentClick ? 'pointer' : 'default',
                            zIndex: 5,
                            padding: '3px 5px',
                          }}
                        >
                          <div className="text-[11px] font-semibold leading-tight truncate" style={{ color: '#111' }}>
                            {hhmm(apt.start_time)} {apt.service_name || 'Termin'}
                          </div>
                          {names.length > 0 && (
                            <div className="text-[11px] leading-tight truncate text-gray-700">
                              {names.join(', ')}
                            </div>
                          )}
                          {trainerName && (
                            <div className="text-[10px] leading-tight truncate text-gray-500">
                              {trainerName}
                            </div>
                          )}
                          {/* Einwirkzeit schraffiert über die betreffenden Segmente */}
                          {segs.map((s, i) => (
                            <div
                              key={`soak-${i}`}
                              data-testid={`resource-soak-${apt.id}-${i}`}
                              title={`Einwirkzeit (${s.name}) — ${s.duration} Min, Platz belegt, Mitarbeiter frei`}
                              style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: `${s.offset * pxPerMin}px`,
                                height: `${s.duration * pxPerMin}px`,
                                pointerEvents: 'none',
                                zIndex: 2,
                                borderTop: '1px dashed rgba(0,0,0,0.25)',
                                borderBottom: '1px dashed rgba(0,0,0,0.25)',
                                backgroundImage:
                                  'repeating-linear-gradient(135deg, rgba(0,0,0,0.12) 0px, rgba(0,0,0,0.12) 5px, transparent 5px, transparent 11px)',
                              }}
                            />
                          ))}
                        </div>
                      );
                    });
                  })()}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ResourceDayView;
